import {
	faCircleCheck,
	faCircleExclamation,
	faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import React from 'react';

import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { LoadingSpinner } from '../display/LoadingSpinner';
import type { MediaUploadEntryModel } from './useMediaUpload';

export interface MediaUploadListProps {
	entries: ReadonlyArray<MediaUploadEntryModel>;
	onDismiss: (key: string) => void;
	onClearFinished?: () => void;
}

/**
 * PD-16339 — one row per file being uploaded: a progress bar while it is sent, then
 * "Checking" while the server looks at it, then done or the reason it was refused.
 */
export function MediaUploadList({
	entries,
	onDismiss,
	onClearFinished,
}: MediaUploadListProps): React.ReactElement | null {
	const { t } = useTranslation();

	if (entries.length === 0) {
		return null;
	}

	const hasFinished = entries.some(
		(entry) => entry.status === 'ready' || entry.status === 'failed',
	);

	return (
		<div
			className="rounded-lg border border-muted bg-surface"
			aria-live="polite"
		>
			<ul className="divide-y divide-muted">
				{entries.map((entry) => (
					<li key={entry.key} className="flex items-start gap-3 px-3 py-2">
						<div className="mt-0.5 w-5 shrink-0 text-center">
							{entry.status === 'ready' && (
								<Icon icon={faCircleCheck} className="text-success" />
							)}
							{entry.status === 'failed' && (
								<Icon icon={faCircleExclamation} className="text-danger" />
							)}
							{(entry.status === 'uploading' ||
								entry.status === 'processing') && (
								<LoadingSpinner size="xs" label={t('Working...')} />
							)}
						</div>
						<div className="min-w-0 flex-1">
							<p className="truncate text-sm text-default">{t(entry.name)}</p>
							{entry.status === 'uploading' && (
								<div
									className="mt-1 h-1.5 w-full overflow-hidden rounded bg-sunken"
									role="progressbar"
									aria-valuemin={0}
									aria-valuemax={100}
									aria-valuenow={Math.round(entry.progress * 100)}
									aria-label={t('Upload progress')}
								>
									<div
										className="h-full bg-accent transition-[width]"
										style={{ width: `${Math.round(entry.progress * 100)}%` }}
									/>
								</div>
							)}
							<p
								className={cn(
									'mt-0.5 text-xs',
									entry.status === 'failed' ? 'text-danger' : 'text-muted',
								)}
							>
								{entry.status === 'uploading' && t('Sending...')}
								{entry.status === 'processing' &&
									t(entry.error ?? 'Checking the file...')}
								{entry.status === 'ready' && t('Added')}
								{entry.status === 'failed' &&
									t(entry.error ?? 'This file could not be used.')}
							</p>
						</div>
						{(entry.status === 'ready' || entry.status === 'failed') && (
							<button
								type="button"
								className="rounded p-1 text-muted hover:text-default focus:outline-none focus:ring-2 focus:ring-accent"
								aria-label={t('Dismiss')}
								onClick={() => {
									onDismiss(entry.key);
								}}
							>
								<Icon icon={faXmark} />
							</button>
						)}
					</li>
				))}
			</ul>
			{hasFinished && onClearFinished != null && (
				<div className="border-t border-muted px-3 py-1.5 text-right">
					<button
						type="button"
						className="text-xs text-accent hover:underline"
						onClick={onClearFinished}
					>
						{t('Clear finished')}
					</button>
				</div>
			)}
		</div>
	);
}
